import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! We'll be in touch soon. 💛");
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="relative bg-blush overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 items-center py-16">
          <div>
            <p className="eyebrow">Our story</p>
            <h1 className="mt-3 font-serif text-4xl lg:text-5xl text-charcoal leading-tight">
              Small things, gathered with care.
            </h1>
            <p className="mt-6 text-charcoal/70 leading-relaxed">
              LuvSprout is a Canadian boutique built on a love of the little details — the ribbon
              tied just so, the notecard that says exactly the right thing, the toy that becomes a
              childhood memory. We curate pieces that feel personal and send every order wrapped by
              hand.
            </p>
            <p className="mt-4 text-charcoal/70 leading-relaxed">
              From our corner of Canada to doorsteps across the country, thank you for letting us be
              part of your moments of giving.
            </p>
          </div>
          <div className="aspect-[4/3] overflow-hidden rounded-sm border border-gold/25">
            <img src={imgUrl("https://images.pexels.com/photos/12080066/pexels-photo-12080066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=940")} alt="Gift boxes" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
        <div className="text-center mb-10">
          <p className="eyebrow">Get in touch</p>
          <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-charcoal">We'd love to hear from you</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1 space-y-6">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-gold mt-0.5" />
              <div>
                <p className="eyebrow text-[0.6rem]">Email</p>
                <p className="text-sm text-charcoal">info@luvsprout.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gold mt-0.5" />
              <div>
                <p className="eyebrow text-[0.6rem]">Ships from</p>
                <p className="text-sm text-charcoal">Canada · CAD ($)</p>
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="md:col-span-2 bg-white border border-soft-border rounded-sm p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs eyebrow text-charcoal/70">Name</Label>
                <Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-none border-charcoal/20" />
              </div>
              <div>
                <Label className="text-xs eyebrow text-charcoal/70">Email</Label>
                <Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-none border-charcoal/20" />
              </div>
            </div>
            <div>
              <Label className="text-xs eyebrow text-charcoal/70">Message</Label>
              <Textarea data-testid="contact-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-none border-charcoal/20" />
            </div>
            <Button type="submit" data-testid="contact-submit" disabled={busy} className="bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide h-11 px-8">
              {busy ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
